import { useAuth0 } from '@auth0/auth0-react'

const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

/**
 * Thin wrapper around fetch that automatically attaches
 * the Auth0 ID token as a Bearer header.
 *
 * We send the ID token (not the access token) because the backend looks up
 * the user by their `email` claim, and only the ID token carries `email`.
 */
export function useApi() {
  // In dev bypass mode we never need a real token — skip the Auth0 hook
  // entirely so Auth0Provider initialisation cannot block the app.
  const auth0 = DEV_BYPASS ? null : useAuth0() // eslint-disable-line react-hooks/rules-of-hooks

  async function getToken(): Promise<string> {
    if (DEV_BYPASS) return 'dev'
    // Ensure a session exists / is refreshed, then read the ID token (has email).
    await auth0!.getAccessTokenSilently()
    const claims = await auth0!.getIdTokenClaims()
    if (!claims?.__raw) throw new Error('No ID token available')
    return claims.__raw
  }

  async function apiFetch<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const token = await getToken()
    const isFormData = options.body instanceof FormData
    const response = await fetch(path, {
      ...options,
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${token}`,
        ...(options.headers ?? {}),
      },
    })

    if (!response.ok) {
      let message = `HTTP ${response.status}`
      try {
        const body = (await response.json()) as { error?: string }
        if (body.error) message = body.error
      } catch {
        // ignore
      }
      console.error(`[API] ${options.method ?? 'GET'} ${path} failed — status ${response.status}: ${message}`)
      throw new Error(message)
    }

    return response.json() as Promise<T>
  }

  async function fetchBlob(path: string): Promise<string> {
    const token = await getToken()
    const response = await fetch(path, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  }

  return { apiFetch, fetchBlob }
}
